/*
 * The server mirror of a draft: what makes a half-written message follow a person to their second
 * device (protocol.md §2.5.6, roadmap S1-05). The server side has been built since S1 — `chat.draft.put`
 * / `.get` / `.list`, `GET /api/chat/drafts`, the `sendBeacon` flush, and the two events that tell the
 * account's other devices that a mirror moved on. This file is the client that uses it.
 *
 * Four rules, and the first one is the only one that matters: **nothing typed is ever overwritten.**
 *
 *  1. *A put when the writing stops.* The local layer writes a draft to IndexedDB 400 ms after the
 *     last keystroke, on blur, on a conversation switch and when the page goes away (`useDraft`), so
 *     a change in the store *is* the "idle" moment. The mirror follows the store, coalesces a burst
 *     into one request, and sends the text with `baseRev` — the revision this device last agreed with
 *     the server on. A stale `baseRev` is refused by the server rather than merged by it.
 *  2. *An adopt only into an empty box.* `chat.draft.changed` carries a digest and no body
 *     (protocol.md §8 R7), so the body is fetched with `chat.draft.get` — and only when this device
 *     holds no text for that target. The draft then lands in the local store, which is what the
 *     composer reads and what the sidebar's pencil reads, so no new surface is needed (P29: there is
 *     no Drafts place).
 *  3. *A conflict copy, never a silent overwrite.* When both sides hold text the server refuses the
 *     put and hands back its own draft; that text is appended under the local text — the same way a
 *     forward writes into a box somebody is using (`DraftStore.append`) — and the merged copy is what
 *     goes up. Both paragraphs are the person's own words, and neither is lost.
 *  4. *A flush on the way out.* `flush()` is awaited before a sign-out ends the session, and a beacon
 *     goes out on `pagehide`; both carry what the socket has not acknowledged yet. The await has a
 *     deadline of its own: ending a session is the gesture, and carrying the last draft out is the
 *     courtesy that may not hold it up.
 *
 * What the mirror does **not** carry across is the attachment tray. An uploaded file is referenced on
 * the wire by id, a name and a caption (protocol.md §2.6) and nothing else, so a chip drawn on the
 * second device would be a file with no size and no kind; the ids ride along with every put, so the
 * mirror's row stays faithful while the device that staged the files is the only one that draws them.
 *
 * It names no module: the two routes, the two socket requests and the event types are chat's, and the
 * mirror reaches them through the transport the boot binds (architecture.md D21).
 */

import type { AttachmentRef as WireAttachmentRef } from "../wire/core.gen.ts"
import { classify, wireAttachmentRef } from "./dispatch.ts"
import type { DraftStore } from "./draftStore.ts"
import { keyOf, parseKey, wireTarget, type WireTarget } from "./targetKey.ts"
import type { DraftRecord, TargetKey } from "./types.ts"

/** How long after a local write the mirror offers it: one request per burst of keystrokes. */
export const MIRROR_IDLE_MS = 800

/** What one flush body may carry — the server's own `maxFlushDrafts` (chat/drafts). */
export const MIRROR_FLUSH_MAX = 20

/** How long a sign-out waits for the last flush before it goes ahead without it (`flush`). */
export const MIRROR_FLUSH_DEADLINE_MS = 1_500

// ---- the wire, as the mirror reads it ----

/**
 * One file of a mirrored draft, in the shape every wire carries a draft's files in: the id the upload
 * answered and the two fields a sender may still change (protocol.md §2.6). The local tray knows more
 * about the file; the wire does not.
 */
export type MirrorAttachment = WireAttachmentRef

/** A draft as the server holds it — `DraftView`, narrowed to the fields the mirror reads. */
export interface MirrorDraft extends WireTarget {
	conv: number
	markdown: string
	attachments?: readonly MirrorAttachment[]
	rev: number
	updatedAt: number
}

/**
 * A retired draft — `DraftTombstone`, and the payload of `chat.draft.deleted`. It names no
 * conversation, which is why a thread's tombstone is matched against the keys this device holds.
 */
export interface MirrorTombstone extends WireTarget {
	rev: number
	deletedAt: number
}

/** One write of the mirror — `DraftPut`. An empty body with no attachments is a delete. */
export interface MirrorPut extends WireTarget {
	markdown: string
	attachments: MirrorAttachment[]
	baseRev: number
}

/** The answer to a put: the revision the server now holds, or that the draft is gone. */
export interface MirrorPutAck {
	rev: number
	updatedAt: number
	deleted: boolean
}

/** The digest of `chat.draft.changed`: a revision and a length, never a body. */
export interface MirrorChanged extends WireTarget {
	conv: number
	rev: number
	bytes: number
}

/**
 * The four calls the mirror makes, bound by the boot to the module that owns them. A rejection is a
 * `WireError`; the conflict is recognised by its code and carries the server's draft as its detail.
 */
export interface DraftMirrorTransport {
	/** Every draft this account holds, with the tombstones that retired the others. */
	list(): Promise<{ drafts: readonly MirrorDraft[]; tombstones: readonly MirrorTombstone[] }>
	/** One draft's body, or null where the server has none. */
	get(target: WireTarget): Promise<MirrorDraft | null>
	/** Writes one draft at `baseRev`. */
	put(put: MirrorPut): Promise<MirrorPutAck>
	/** The page going away: one request that cannot be awaited and whose answer nobody reads. */
	beacon(puts: readonly MirrorPut[]): void
	/** The same write, awaited — the sign-out path, which has to land while the session still exists. */
	flush(puts: readonly MirrorPut[]): Promise<void>
}

/**
 * What the mirror needs of the client store (`core/store/store.ts`): the tap that reports every
 * applied event, and the connection, whose first "open" is the moment a catch-up makes sense.
 */
export interface MirrorClient {
	onEvent(listener: (event: { type: string; data: unknown }, result: unknown) => void): () => void
	api: {
		getState(): { connection: string }
		subscribe(listener: (model: { connection: string }) => void): () => void
	}
}

export interface DraftMirrorDeps {
	store: DraftStore
	client: MirrorClient
	transport: DraftMirrorTransport
	/** How long a burst of local writes is coalesced for; `MIRROR_IDLE_MS` unless a test says otherwise. */
	idleMs?: number
}

/** What the server holds for one target, as far as this device knows. */
interface Agreed {
	rev: number
	markdown: string
	ids: readonly number[]
}

export class DraftMirror {
	private readonly store: DraftStore
	private readonly client: MirrorClient
	private readonly transport: DraftMirrorTransport
	private readonly idleMs: number
	/** Per target: the text the server has, at the revision it has it. */
	private readonly agreed = new Map<TargetKey, Agreed>()
	/** A body the server refused for good (too large, a target it will not take): not offered again. */
	private readonly refused = new Map<TargetKey, string>()
	private readonly stops: (() => void)[] = []
	private timer: ReturnType<typeof setTimeout> | undefined
	private pushing = false
	/** A local write that landed while a request was out: the round after this one takes it. */
	private again = false
	private open = false

	constructor(deps: DraftMirrorDeps) {
		this.store = deps.store
		this.client = deps.client
		this.transport = deps.transport
		this.idleMs = deps.idleMs ?? MIRROR_IDLE_MS
	}

	/** Follows the local store, the event tap, the connection and the page's last moment. */
	start(): void {
		if (this.stops.length > 0) return
		this.stops.push(this.store.subscribe(() => this.schedule()))
		this.stops.push(
			this.client.onEvent((event, result) => {
				// Only what the store actually applied: an event held while a conversation loads is
				// replayed once it settles, and acting on both copies would fetch the same body twice.
				if (result === "applied") this.heard(event)
			}),
		)
		this.open = this.client.api.getState().connection === "open"
		this.stops.push(
			this.client.api.subscribe((model) => {
				const open = model.connection === "open"
				if (open === this.open) return
				this.open = open
				// A socket that has just caught up is also a device that may have missed a draft
				// entirely — a cold boot replays no event for one written before it existed.
				if (open) void this.sync()
			}),
		)
		if (this.open) void this.sync()
		if (typeof window !== "undefined") {
			const onPageHide = () => this.beacon()
			window.addEventListener("pagehide", onPageHide)
			this.stops.push(() => window.removeEventListener("pagehide", onPageHide))
		}
	}

	stop(): void {
		if (this.timer !== undefined) clearTimeout(this.timer)
		this.timer = undefined
		for (const stop of this.stops) stop()
		this.stops.length = 0
	}

	/**
	 * Reads the whole mirror and reconciles it with this device: an empty box takes the server's
	 * draft, a box with text keeps it and gets the server's copy appended, and what the server does
	 * not hold yet goes up afterwards. A failed read changes nothing — the next "open" asks again.
	 */
	async sync(): Promise<void> {
		await this.store.ready
		let answer: { drafts: readonly MirrorDraft[]; tombstones: readonly MirrorTombstone[] }
		try {
			answer = await this.transport.list()
		} catch {
			return
		}
		for (const draft of answer.drafts) await this.take(draft)
		for (const tombstone of answer.tombstones) await this.retire(tombstone)
		this.schedule()
	}

	/**
	 * Sends what the socket has not acknowledged, and waits for it — but never for longer than
	 * `MIRROR_FLUSH_DEADLINE_MS`. The one caller is the sign-out: after it the session is gone and the
	 * mirror cannot be written any more, so the wait is worth having — and it is a courtesy, not a
	 * step of signing out. A request that hangs would otherwise hold the gesture open for as long as
	 * the browser's own timeout (the api layer sets none), and the person who pressed *Sign out* would
	 * watch nothing happen while the session they wanted ended stayed live. Past the deadline the
	 * request is simply left running; nothing is lost either way, because every draft in it is on this
	 * device's disk and the `pagehide` beacon carries it again.
	 */
	async flush(): Promise<void> {
		const puts = this.body()
		if (puts.length === 0) return
		let timer: ReturnType<typeof setTimeout> | undefined
		try {
			await Promise.race([
				this.transport.flush(puts),
				new Promise<void>((done) => {
					timer = setTimeout(done, MIRROR_FLUSH_DEADLINE_MS)
				}),
			])
		} catch {
			// Nothing is lost by a failed flush: every draft in it is on this device's disk.
		} finally {
			if (timer !== undefined) clearTimeout(timer)
		}
	}

	/** The same drafts on the way out of the page, where nothing can be awaited. */
	beacon(): void {
		const puts = this.body()
		if (puts.length > 0) this.transport.beacon(puts)
	}

	// ---- what the server says ----

	private heard(event: { type: string; data: unknown }): void {
		if (event.type === "chat.draft.changed") {
			const changed = event.data as Partial<MirrorChanged>
			if (isTarget(changed) && typeof changed.rev === "number") {
				void this.changed({
					targetKind: changed.targetKind,
					targetId: changed.targetId,
					conv: typeof changed.conv === "number" ? changed.conv : 0,
					rev: changed.rev,
					bytes: typeof changed.bytes === "number" ? changed.bytes : 1,
				})
			}
			return
		}
		if (event.type !== "chat.draft.deleted") return
		const deleted = event.data as Partial<MirrorTombstone>
		if (isTarget(deleted) && typeof deleted.rev === "number") {
			void this.retire({
				targetKind: deleted.targetKind,
				targetId: deleted.targetId,
				rev: deleted.rev,
				deletedAt: typeof deleted.deletedAt === "number" ? deleted.deletedAt : 0,
			})
		}
	}

	/** Another device moved a draft on. Only an empty box asks for the body. */
	private async changed(changed: MirrorChanged): Promise<void> {
		const key = keyFor(changed, changed.conv)
		if (key === null) return
		// This device's own write, echoed back to it, or one it has already taken.
		if (changed.rev <= (this.agreed.get(key)?.rev ?? 0)) return
		// A box with text in it is never overwritten and never interrupted: the server's copy arrives
		// with the next put, as the conflict copy (rule 3 of this file's head).
		if (hasText(this.store.get(key))) return
		if (changed.bytes === 0) return
		let draft: MirrorDraft | null
		try {
			draft = await this.transport.get(wireTargetOf(changed))
		} catch {
			return
		}
		if (draft !== null) await this.take(draft)
	}

	/**
	 * One draft the server holds, against what this device holds. `take` is the only way a remote
	 * draft enters the local store, and it is where the no-overwrite rule lives: a box with text in it
	 * keeps every character of it.
	 */
	private async take(draft: MirrorDraft): Promise<void> {
		const key = keyFor(draft, draft.conv)
		if (key === null) return
		const local = this.store.get(key)
		if (!hasText(local)) {
			if (draft.markdown === "") return
			// The agreement is recorded before the write, never after it: the write wakes the store's
			// readers, one of which is this mirror's own scheduler, and an adopt that looked unrecorded
			// for a moment would be offered straight back to the server.
			//
			// The attachments are deliberately not seeded: see this file's head.
			this.note(key, draft.rev, draft.markdown, [])
			await this.store.put(key, draft.markdown, [])
			await this.store.stamp(key, draft.rev)
			return
		}
		if (local.markdown.includes(draft.markdown)) {
			// The server's text is already in the box — usually this device's own, after a reload that
			// outlived the revision. Take the revision and let the local text go up under it.
			this.note(key, draft.rev, draft.markdown, idsOf(draft.attachments))
			await this.store.stamp(key, draft.rev)
			return
		}
		// Both sides hold text: the copy, under what is in the box, and nothing replaced.
		this.note(key, draft.rev, draft.markdown, idsOf(draft.attachments))
		if ((await this.store.append(key, draft.markdown)) !== "stored") {
			// The two texts together are over the local store's cap, so nothing was written — and the
			// agreement must not stand either. Left standing it would say "the server's text is in this
			// box" about a box that does not hold it, and the next round would offer the local text at
			// the server's own revision, which the server would accept over its copy: the one way this
			// file could lose a paragraph. Instead both copies stay where they are, and this target is
			// not offered again until its text changes — the same answer `offer` gives a refusal no
			// repeat can outlive. (Only the merge can be too large: the server's own cap is the smaller
			// one, so a draft arriving on its own always fits.)
			this.agreed.delete(key)
			this.refused.set(key, local.markdown)
			return
		}
		await this.store.stamp(key, draft.rev)
	}

	/**
	 * A draft was retired elsewhere — emptied, deleted, or sent, which tombstones the draft it was
	 * written in (protocol.md §8 R14). The local row goes only when this device knows it to be the
	 * mirrored copy; anything this device has typed since stays, and its agreement is reset so the
	 * text can be mirrored again (a put with `baseRev` 0 clears the tombstone).
	 */
	private async retire(tombstone: MirrorTombstone): Promise<void> {
		for (const key of this.keysOf(tombstone)) {
			const row = this.store.get(key)
			if (row === undefined) {
				this.agreed.delete(key)
				continue
			}
			const agreed = this.agreed.get(key)
			const mirrored = agreed !== undefined && agreed.markdown === row.markdown
			if (mirrored && (row.rev ?? 0) <= tombstone.rev) {
				this.agreed.delete(key)
				await this.store.remove(key)
				continue
			}
			this.agreed.delete(key)
			await this.store.stamp(key, 0)
		}
	}

	// ---- what this device says ----

	private schedule(): void {
		if (this.timer !== undefined) return
		this.timer = setTimeout(() => {
			this.timer = undefined
			void this.push()
		}, this.idleMs)
	}

	private async push(): Promise<void> {
		if (!this.open) return
		// One round at a time: a keystroke that lands while a request is out is not dropped, it is the
		// next round's — offering the same target twice at once would put two writes at one `baseRev`.
		if (this.pushing) {
			this.again = true
			return
		}
		this.pushing = true
		try {
			for (const { key, put } of this.pending()) await this.offer(key, put)
		} finally {
			this.pushing = false
			if (this.again) {
				this.again = false
				this.schedule()
			}
		}
	}

	private async offer(key: TargetKey, put: MirrorPut): Promise<void> {
		let ack: MirrorPutAck
		try {
			ack = await this.transport.put(put)
		} catch (err) {
			const remote = conflictDraft(err)
			if (remote !== null) {
				await this.take(remote)
				this.schedule()
				return
			}
			// A refusal a repeat could outlive is left for the next idle; a final one — a body over the
			// server's cap, a target it will not take — is remembered, so the mirror does not ask again
			// every time the person touches that draft. The text stays in the box either way.
			if (!classify(err).retryable) this.refused.set(key, put.markdown)
			return
		}
		this.refused.delete(key)
		if (ack.deleted) {
			// The server holds a tombstone at or above this `baseRev`: the draft was retired elsewhere
			// and this text is something the person has typed since. Forget the revision, so the next
			// offer goes with `baseRev` 0 and clears the tombstone instead of being refused by it.
			this.agreed.delete(key)
			await this.store.stamp(key, 0)
			if (put.baseRev > 0 && hasText(this.store.get(key))) this.schedule()
			return
		}
		// What the server now holds is what was *sent*, never what the box holds at this moment: a
		// keystroke can land during the round trip, and recording that text as the server's would leave
		// it sitting on this device for ever.
		this.note(
			key,
			ack.rev,
			put.markdown,
			put.attachments.map((a) => a.id),
		)
		await this.store.stamp(key, ack.rev)
	}

	/** Every draft whose text the server does not hold, newest first; a deletion is an empty body. */
	private pending(): { key: TargetKey; put: MirrorPut }[] {
		const out: { key: TargetKey; put: MirrorPut }[] = []
		const seen = new Set<TargetKey>()
		for (const row of this.store.all()) {
			seen.add(row.key)
			const target = wireTargetFor(row.key)
			if (target === null) continue
			if (this.refused.get(row.key) === row.markdown) continue
			const agreed = this.agreed.get(row.key)
			const ids = row.attachments.map((a) => a.id)
			if (agreed && agreed.markdown === row.markdown && sameIds(agreed.ids, ids)) continue
			out.push({
				key: row.key,
				put: {
					...target,
					markdown: row.markdown,
					attachments: row.attachments.map(wireAttachmentRef),
					baseRev: row.rev ?? 0,
				},
			})
		}
		// A draft the server still holds and this device has cleared: an empty body is the delete.
		for (const [key, agreed] of this.agreed) {
			if (seen.has(key)) continue
			const target = wireTargetFor(key)
			if (target === null) continue
			out.push({ key, put: { ...target, markdown: "", attachments: [], baseRev: agreed.rev } })
		}
		return out
	}

	/** One flush body: the newest drafts first, capped at what the route accepts. */
	private body(): MirrorPut[] {
		return this.pending()
			.slice(0, MIRROR_FLUSH_MAX)
			.map(({ put }) => put)
	}

	private note(key: TargetKey, rev: number, markdown: string, ids: readonly number[]): void {
		this.agreed.set(key, { rev, markdown, ids })
	}

	/**
	 * The local keys a tombstone can mean. It carries no conversation, so a thread's draft is found by
	 * its root id among the keys this device holds — the root is a message id and names one thread.
	 */
	private keysOf(tombstone: MirrorTombstone): TargetKey[] {
		if (tombstone.targetKind !== "thread") {
			const key = keyFor(tombstone, tombstone.targetId)
			return key === null ? [] : [key]
		}
		const suffix = `:t:${String(tombstone.targetId)}`
		const keys = new Set<TargetKey>()
		for (const row of this.store.all()) if (row.key.endsWith(suffix)) keys.add(row.key)
		for (const key of this.agreed.keys()) if (key.endsWith(suffix)) keys.add(key)
		return [...keys]
	}
}

let installed: DraftMirror | undefined

/**
 * Starts the one mirror of this document, over the application's draft store. Called by the boot,
 * where the socket and the client store exist; a second call replaces the first.
 */
export function installDraftMirror(deps: DraftMirrorDeps): DraftMirror {
	installed?.stop()
	installed = new DraftMirror(deps)
	installed.start()
	return installed
}

/** The running mirror, or nothing where the boot has not installed one (a test, a signed-out page). */
export function draftMirror(): DraftMirror | undefined {
	return installed
}

// ---- the pure pieces ----

/** True while a draft holds words. An empty box is what a remote draft may be adopted into. */
function hasText(record: DraftRecord | undefined): record is DraftRecord {
	return record !== undefined && record.markdown.trim() !== ""
}

function idsOf(attachments: readonly MirrorAttachment[] | undefined): number[] {
	return (attachments ?? []).map((a) => a.id)
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
	return a.length === b.length && a.every((id, i) => id === b[i])
}

/** A payload that names a target — the shape every draft frame shares. */
function isTarget<T extends Partial<WireTarget>>(value: T): value is T & WireTarget {
	return (
		(value.targetKind === "conversation" ||
			value.targetKind === "thread" ||
			value.targetKind === "messageEdit") &&
		typeof value.targetId === "number"
	)
}

/**
 * The local key of a wire target. A thread's key carries the conversation as well as the root
 * message, so a frame without one names no local draft.
 */
function keyFor(target: WireTarget, conv: number): TargetKey | null {
	switch (target.targetKind) {
		case "conversation":
			return target.targetId > 0 ? keyOf({ kind: "conversation", conv: target.targetId }) : null
		case "thread":
			return conv > 0 && target.targetId > 0
				? keyOf({ kind: "thread", conv, threadRootId: target.targetId })
				: null
		case "messageEdit":
			return target.targetId > 0 ? keyOf({ kind: "messageEdit", messageId: target.targetId }) : null
	}
}

function wireTargetOf(target: WireTarget): WireTarget {
	return { targetKind: target.targetKind, targetId: target.targetId }
}

function wireTargetFor(key: TargetKey): WireTarget | null {
	const target = parseKey(key)
	return target === null ? null : wireTarget(target)
}

/**
 * The server's own draft out of a refused put (`err conflict error.chat.draftConflict`,
 * protocol.md §2.5.6). Anything that is not that conflict, or whose detail is not a draft, is a
 * failure like any other.
 */
function conflictDraft(err: unknown): MirrorDraft | null {
	if (typeof err !== "object" || err === null) return null
	const { code, detail } = err as { code?: unknown; detail?: unknown }
	if (code !== "conflict" || typeof detail !== "object" || detail === null) return null
	const view = detail as Partial<MirrorDraft>
	if (!isTarget(view) || typeof view.markdown !== "string" || typeof view.rev !== "number")
		return null
	return {
		targetKind: view.targetKind,
		targetId: view.targetId,
		conv: typeof view.conv === "number" ? view.conv : 0,
		markdown: view.markdown,
		...(view.attachments !== undefined && { attachments: view.attachments }),
		rev: view.rev,
		updatedAt: typeof view.updatedAt === "number" ? view.updatedAt : 0,
	}
}
